type AutocompleteItem = {
  text: string;
  value: string;
};

type CbarMenuElement = HTMLElement & {open?: boolean};

type CbarAutocompleteElement = HTMLElement & {
  value?: string;
  open?: boolean;
  fixedMenuPosition?: boolean;
  datasource?: AutocompleteItem[];
  committedText?: string;
  committedValue?: string;
  /** True when the autocomplete input or its menu has focus. */
  isFocused?: boolean;
  pauseOpening?: (timeout?: number) => void;
  clearPauseOpening?: () => void;
  dataset: DOMStringMap;
};

const BOUND_ATTR = 'data-cbar-autocomplete-bridge-bound';
const EXCLUSIVE_WIRED_ATTR = 'data-cbar-autocomplete-exclusive-wired';

let autocompletePopoverDismissWired = false;

function parseDatasource(autocomplete: CbarAutocompleteElement): AutocompleteItem[] {
  if (Array.isArray(autocomplete.datasource) && autocomplete.datasource.length > 0) {
    return autocomplete.datasource;
  }

  const raw = autocomplete.getAttribute('datasource');
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as AutocompleteItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function resolveValueInput(autocomplete: CbarAutocompleteElement): HTMLInputElement | null {
  const inputId = autocomplete.dataset.valueInput;
  if (!inputId) {
    return null;
  }
  const el = document.getElementById(inputId);
  return el instanceof HTMLInputElement ? el : null;
}

function isCbarAutocomplete(el: Element | null | undefined): el is CbarAutocompleteElement {
  return !!el && el.tagName === 'CBAR-AUTOCOMPLETE';
}

function getAutocompleteMenu(autocomplete: CbarAutocompleteElement): CbarMenuElement | null {
  return (autocomplete.shadowRoot?.querySelector('cbar-menu') as CbarMenuElement | null) ?? null;
}

function menuIsOpen(autocomplete: CbarAutocompleteElement): boolean {
  if (autocomplete.open) {
    return true;
  }
  return Boolean(getAutocompleteMenu(autocomplete)?.open);
}

/**
 * Close host + inner menu, and pause reopen. Without pauseOpening, willUpdate
 * sees shouldOpen=true (field still focused) and snaps open back on — the
 * Instruments→Accounts→Campaigns freeze loop.
 */
function forceCloseAutocomplete(autocomplete: CbarAutocompleteElement): void {
  autocomplete.open = false;

  const menu = getAutocompleteMenu(autocomplete);
  if (menu?.open) {
    menu.open = false;
  }

  autocomplete.pauseOpening?.(400);
}

/** Match date filters: fixed menus in filter grids avoid top-layer popover fights. */
function ensureFilterAutocompleteMenuPosition(autocomplete: CbarAutocompleteElement): void {
  if (
    autocomplete.fixedMenuPosition
    || autocomplete.hasAttribute('fixed-menu-position')
    || autocomplete.getAttribute('data-cbar-autocomplete-menu-position-set') === 'true'
  ) {
    return;
  }

  const inFilterGrid = autocomplete.closest(
    'form.cbar-filter-form cbar-form-grid, form[name="filter_form"] cbar-form-grid',
  );
  if (!inFilterGrid) {
    return;
  }

  autocomplete.fixedMenuPosition = true;
  autocomplete.setAttribute('fixed-menu-position', '');
  autocomplete.setAttribute('data-cbar-autocomplete-menu-position-set', 'true');
}

function closeOpenAutocompletesExcept(active: CbarAutocompleteElement | null): void {
  document.querySelectorAll('cbar-autocomplete').forEach((node) => {
    if (!isCbarAutocomplete(node) || node === active || !menuIsOpen(node)) {
      return;
    }
    forceCloseAutocomplete(node);
  });
}

function pointInClientRect(x: number, y: number, rect: DOMRect): boolean {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function findAutocompletesUnderPointer(x: number, y: number): CbarAutocompleteElement[] {
  const hits: CbarAutocompleteElement[] = [];
  document.querySelectorAll('cbar-autocomplete').forEach((node) => {
    if (!isCbarAutocomplete(node)) {
      return;
    }
    if (pointInClientRect(x, y, node.getBoundingClientRect())) {
      hits.push(node);
    }
  });
  return hits;
}

/** Prefer the closed sibling when an open menu popover overlaps another field. */
function resolveTargetAutocompleteFromPointer(x: number, y: number): CbarAutocompleteElement | null {
  const hits = findAutocompletesUnderPointer(x, y);
  if (hits.length === 0) {
    return null;
  }
  return hits.find((input) => !menuIsOpen(input)) ?? hits[hits.length - 1];
}

function isPointerInsideOpenAutocompleteMenu(
  path: EventTarget[],
  autocomplete: CbarAutocompleteElement,
): boolean {
  const menu = getAutocompleteMenu(autocomplete);
  if (menu && path.includes(menu)) {
    return true;
  }

  // Popover retargeting can put items in the path without the menu host.
  return path.some((node) => {
    if (!(node instanceof Element) || node.tagName !== 'CBAR-MENU-ITEM') {
      return false;
    }
    if (menu?.contains(node)) {
      return true;
    }
    const root = node.getRootNode();
    return root instanceof ShadowRoot && root.host === menu;
  });
}

function findOpenAutocompleteOwningPointer(path: EventTarget[]): CbarAutocompleteElement | null {
  let owner: CbarAutocompleteElement | null = null;
  document.querySelectorAll('cbar-autocomplete').forEach((node) => {
    if (owner || !isCbarAutocomplete(node) || !menuIsOpen(node)) {
      return;
    }
    if (isPointerInsideOpenAutocompleteMenu(path, node)) {
      owner = node;
    }
  });
  return owner;
}

function findAutocompleteInComposedPath(path: EventTarget[]): CbarAutocompleteElement | null {
  for (const entry of path) {
    if (entry instanceof HTMLElement && isCbarAutocomplete(entry)) {
      return entry;
    }
  }
  return null;
}

/**
 * Same pattern as date exclusive popovers: close sibling top-layer menus on
 * capture-phase pointerdown so they cannot swallow the click meant for the
 * next Instruments/Accounts/Campaigns field (or freeze the page in an
 * open/close loop).
 */
function wireAutocompletePopoverDismiss(): void {
  if (autocompletePopoverDismissWired) {
    return;
  }
  autocompletePopoverDismissWired = true;

  document.addEventListener('pointerdown', (event) => {
    if (!(event instanceof PointerEvent) || event.button !== 0) {
      return;
    }

    const path = event.composedPath();
    const pointerTarget = resolveTargetAutocompleteFromPointer(event.clientX, event.clientY);
    const pathInput = findAutocompleteInComposedPath(path);
    const menuOwner = findOpenAutocompleteOwningPointer(path);

    // Popover covers a sibling field: path hits the open menu, but the pointer
    // is over another autocomplete — dismiss the overlay and activate the sibling
    // (same failure mode as date filters overlapping the next field).
    if (menuOwner && pointerTarget && pointerTarget !== menuOwner) {
      forceCloseAutocomplete(menuOwner);
    } else if (menuOwner) {
      closeOpenAutocompletesExcept(menuOwner);
      return;
    }

    const activeInput = pointerTarget ?? pathInput;
    let closedSiblingForPointerTarget = false;

    document.querySelectorAll('cbar-autocomplete').forEach((node) => {
      if (!isCbarAutocomplete(node) || !menuIsOpen(node)) {
        return;
      }

      if (activeInput && activeInput !== node) {
        if (pointerTarget === activeInput) {
          closedSiblingForPointerTarget = true;
        }
        forceCloseAutocomplete(node);
        return;
      }

      if (!activeInput) {
        forceCloseAutocomplete(node);
      }
    });

    if (activeInput) {
      closeOpenAutocompletesExcept(activeInput);

      if (closedSiblingForPointerTarget) {
        requestAnimationFrame(() => {
          activeInput.clearPauseOpening?.();
          activeInput.open = true;
        });
      }
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    const path = event.composedPath();
    const menuOwner = findOpenAutocompleteOwningPointer(path);
    const activeInput = menuOwner ?? findAutocompleteInComposedPath(path);
    if (activeInput) {
      closeOpenAutocompletesExcept(activeInput);
    }
  }, true);
}

function setHiddenValue(valueInput: HTMLInputElement, nextValue: string): void {
  if (valueInput.value === nextValue) {
    return;
  }
  valueInput.value = nextValue;
  valueInput.dispatchEvent(new Event('change', {bubbles: true}));
}

function setAutocompleteDisplay(autocomplete: CbarAutocompleteElement, nextText: string): void {
  // Avoid re-assigning the same label — a second write fires `input` and can
  // flip cbar-autocomplete into filter mode (one matching suggestion).
  if (autocomplete.value !== nextText) {
    autocomplete.value = nextText;
  }
}

function syncFromSelection(autocomplete: CbarAutocompleteElement, selectedId: string): void {
  const valueInput = resolveValueInput(autocomplete);
  if (!valueInput) {
    return;
  }

  const items = parseDatasource(autocomplete);
  const match = items.find((item) => item.value === selectedId);
  const nextValue = selectedId || valueInput.value || '';
  setHiddenValue(valueInput, nextValue);
  if (match) {
    setAutocompleteDisplay(autocomplete, match.text);
    autocomplete.committedText = match.text;
    autocomplete.committedValue = match.value;
  }
}

/** Update only the hidden input from typed text — do not rewrite the field label. */
function syncHiddenFromTypedText(autocomplete: CbarAutocompleteElement): void {
  const valueInput = resolveValueInput(autocomplete);
  if (!valueInput) {
    return;
  }

  const items = parseDatasource(autocomplete);
  const typed = (autocomplete.value ?? '').trim().toLowerCase();
  if (typed === '') {
    return;
  }

  const match = items.find(
    (item) => item.text.toLowerCase() === typed || item.value.toLowerCase() === typed,
  );
  if (match) {
    setHiddenValue(valueInput, match.value);
    autocomplete.committedText = match.text;
    autocomplete.committedValue = match.value;
  }
}

function bindAutocomplete(autocomplete: CbarAutocompleteElement): void {
  if (autocomplete.dataset.cbarAutocompleteBridgeBound === 'true') {
    return;
  }
  if (!autocomplete.dataset.valueInput) {
    return;
  }
  autocomplete.dataset.cbarAutocompleteBridgeBound = 'true';
  autocomplete.setAttribute(BOUND_ATTR, 'true');
  ensureFilterAutocompleteMenuPosition(autocomplete);

  autocomplete.addEventListener('item-selected', ((event: CustomEvent<{value?: string}>) => {
    syncFromSelection(autocomplete, String(event.detail?.value ?? ''));
  }) as EventListener);

  autocomplete.addEventListener('focusout', () => {
    window.setTimeout(() => {
      if (autocomplete.isFocused || menuIsOpen(autocomplete)) {
        return;
      }
      syncHiddenFromTypedText(autocomplete);
    }, 150);
  });

  if (autocomplete.getAttribute(EXCLUSIVE_WIRED_ATTR) !== 'true') {
    autocomplete.setAttribute(EXCLUSIVE_WIRED_ATTR, 'true');
    autocomplete.addEventListener('pointerdown', () => {
      closeOpenAutocompletesExcept(autocomplete);
    }, true);
  }
}

/** Resolve typed labels into hidden values (e.g. before reading filter params on submit). */
export function syncCbarAutocompleteFromTypedText(root: ParentNode = document): void {
  root.querySelectorAll<CbarAutocompleteElement>('cbar-autocomplete[data-value-input]').forEach((autocomplete) => {
    syncHiddenFromTypedText(autocomplete);
  });
}

/**
 * Syncs hidden value inputs for every `cbar-autocomplete[data-value-input]` and
 * dispatches bubbling `change` on the hidden input so existing page listeners keep working.
 * Also wires exclusive top-layer dismiss (same idea as date filter popovers).
 */
export function setupCbarAutocompleteBridge(): void {
  wireAutocompletePopoverDismiss();

  document.querySelectorAll<CbarAutocompleteElement>('cbar-autocomplete[data-value-input]').forEach(bindAutocomplete);

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement) {
        syncCbarAutocompleteFromTypedText(form);
      }
    },
    true,
  );

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }
        if (node.matches('cbar-autocomplete[data-value-input]')) {
          bindAutocomplete(node as CbarAutocompleteElement);
        }
        node.querySelectorAll?.('cbar-autocomplete[data-value-input]').forEach((el) => {
          bindAutocomplete(el as CbarAutocompleteElement);
        });
      });
    }
  });

  observer.observe(document.documentElement, {childList: true, subtree: true});
}
